import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { ButtonLink } from 'stile-shared/src/components/2020/common/ButtonLink';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import {
  H3,
  Lead,
  P,
  PageHeading,
  Spacer,
} from 'stile-shared/src/components/2020/common/Primitives';

import { getPageData } from 'utils/getPageData';

import { PageLayout } from 'templates/2020/PageLayout';

export { Head } from 'templates/2020/Head';

function LunariaPage(props: PageProps) {
  const { hero } = getPageData(props.location.pathname);

  return (
    <PageLayout {...props}>
      <TeaserBlock
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/lunaria/lunaria_hero.png"
            alt=""
          />
        }
      >
        <PageHeading>{hero.title}</PageHeading>
        <Lead>{hero.text}</Lead>
      </TeaserBlock>

      <TextBlock>
        <P>
          Over the next two years, your students will have the opportunity to embark on an epic
          adventure. In 2025, they’ll select seeds to journey to the Moon. By 2026, these seeds will
          reach the Moon and students will be able to monitor their growth in the challenging lunar
          environment!
        </P>
      </TextBlock>

      <TextBlock title="Lunaria One: Going beyond Earth for a sustainable future" variant="dark">
        <P>
          Exploration of space and survival in barren landscapes are fundamental to humanity’s
          curious nature. With Stile’s exclusive partnership with{' '}
          <Link openInNewTab variant="inherit" url="https://lunaria.one/">
            LunariaOne
          </Link>
          , students will select seeds that will (hopefully!) grow on the Moon. This mission is a
          key step in determining whether we can grow a food supply in space that could sustain a
          human colony beyond planet Earth. It will serve as an important test case for similar
          explorations of Mars in the years to come!
        </P>
        <P>
          Being the first of its kind, this mission will include a set of seeds and plants carefully
          selected to withstand the transit to the Moon and survival on its surface. By landing our
          payload on the Lunar surface, we aim to show growth (germination and/or growth from
          dormant plant state) within the first 72 hours of landing.
        </P>
      </TextBlock>

      <TextBlock title="Stile’s classroom instructional and teacher support materials">
        <P>
          First and foremost, with this partnership, your students really will have their say in
          which seeds get sent to the Moon! They’ll be over the Moon with the opportunity to take
          part in this real-world experience.
        </P>
        <Spacer />
        <H3>New Plants unit</H3>
        <P>
          In preparation for the Lunaria One expedition to the Moon, we’re updating our Plants unit
          to provide relevant, curriculum-aligned coursework. Through our new Plants unit, your
          students will learn about plant biology, explore the biological challenges of growing
          plants on the Moon, and unpack the engineering challenges involved with the mission.
        </P>
        <P>
          Like every Stile unit, the Plants unit will be accompanied by everything you need to teach
          it: lesson plans, videos, workbooks, assessments, challenge-based learning activities, lab
          kits, and teaching notes. This Plants unit will be directly aligned with the{' '}
          <Link url="/resources/ac9.0-2024/">Australian Curriculum Version 9.0</Link> and{' '}
          <Link url="/resources/new-nsw-syllabus/">2026 NSW Syllabus</Link>, and is supplemental for
          the <Link url="/resources/vic-2024/">VIC Curriculum</Link>.
        </P>
        <P>Take a look at the updated Plants unit outline below.</P>
        <StaticImage
          src="../../../../stile-shared/assets/images/lunaria/lunaria_lesson.png"
          alt=""
        />
        <Spacer />
        <H3>A data science and citizen science focus</H3>
        <P>
          Through this new unit, your students will participate in a{' '}
          <strong>citizen science project to hone their data science skills</strong>. They’ll
          conduct investigations on key factors that influence plant growth and interpret data to
          determine the best seeds to grow on the Moon. Their work will contribute to deciding which
          seeds and plants will be taken to the Moon on a real rocket!{' '}
        </P>
        <P>
          Not just for Stile schools, this project will reach global proportions with citizen
          scientists and school children from around the world to help us test a selection of plant
          species to identify varieties best suited for growth on the Lunar surface. Opening the
          scientific process to the public will allow us to provide unique educational
          opportunities, as well as inspire innovation.
        </P>
        <P>
          This new Plants unit will be ready for you to use in class with your students in 2025.
          Join us for this extraterrestrial adventure! 🚀
        </P>
      </TextBlock>

      <CtaBlock
        title="Follow along"
        text="Join our mailing list to follow along with Stile's extraterrestrial journey"
        button={
          <ButtonLink variant="dark" url="https://stileapp.com/go/LunariaOneform">
            Join mailing list
          </ButtonLink>
        }
      />
    </PageLayout>
  );
}

export default LunariaPage;
